import { useEffect, useState } from 'react';

const steps = [
  {
    id: 1,
    title: 'Choose Template',
    description: 'Select from 50+ professionally designed templates for any industry — eCommerce, restaurants, fitness, real estate, and more.',
    icon: 'ri-layout-grid-line',
    color: 'from-purple-500 to-pink-500',
  },
  {
    id: 2,
    title: 'Customize',
    description: 'Use our intuitive drag-and-drop builder to personalize every element. Add your branding, content, and features without writing code.',
    icon: 'ri-edit-box-line',
    color: 'from-pink-500 to-orange-500',
  },
  {
    id: 3,
    title: 'Publish',
    description: 'Launch your app to iOS App Store and Google Play Store with one click. We handle all the technical details for you.',
    icon: 'ri-rocket-line',
    color: 'from-orange-500 to-red-500',
  },
];

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('how-it-works');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-purple-100 rounded-full text-purple-700 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <i className="ri-lightbulb-line"></i>
            <span className="text-sm font-semibold">Simple Process</span>
          </div>
          <h2 className={`text-5xl font-extrabold text-gray-900 mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            How It Works
          </h2>
          <p className={`text-xl text-gray-600 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            From idea to app store in three simple steps. No technical knowledge required.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
          {/* Connection Lines */}
          <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-200 via-pink-200 to-orange-200 -z-10"></div>

          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {/* Step Card */}
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 group">
                {/* Step Number */}
                <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <i className={`${step.icon} text-3xl text-white`}></i>
                </div>

                {/* Step Number Badge */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-gray-900 to-gray-700 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg border-4 border-white">
                  {step.id}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>

              {/* Arrow */}
              {index < steps.length - 1 && (
                <div className="hidden md:flex absolute -right-6 top-24 w-12 h-12 items-center justify-center text-purple-300 z-10">
                  <i className="ri-arrow-right-line text-3xl"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <a
            href="/get-started"
            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-xl shadow-purple-500/30 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer font-semibold"
          >
            Get Started Now
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}